import { useId, useState } from 'react';
import type { TrendMonth } from '@dru/types';
import { ErrorState, cn } from '@dru/ui';
import { CERTIFIED, CHROME, SERIES, TREATMENTS } from './palette';
import { Marker, Swatch } from './Marker';
import {
  formatCount, smoothAreaPath, smoothPath, niceMax, spreadLabels, ticks, xPositions, yScale,
} from './scale';
import { useContainerWidth } from './useContainerWidth';

/**
 * Twelve months of examinations: three lines for what was raised, by type,
 * a fourth for treatments given at the OHC, over a quiet area for what the
 * physician signed off.
 *
 * One axis. Every series is a count of people seen in the month, so they
 * share a scale and the gap between the lines and the area is the backlog,
 * visible without a second axis to misread.
 *
 * Drawn in pixels against the container's measured width, so it fills a
 * card on a desktop and a phone alike while the text stays legible. Hover a
 * month for every value; the table view is the same data for anyone who
 * would rather read it, and for a screen reader.
 */
const SPANS = [3, 6, 12] as const;
type Span = (typeof SPANS)[number];
const SPAN_KEY = 'dru.trend.span';

export function TrendChart({ months, loading, error, onRetry, className }: {
  months: TrendMonth[] | null | undefined;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
}) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  // How far back to look: the latest 3, 6 or 12 of the months the API sent.
  // Remembered per reader, like the sidebar's fold.
  const [span, setSpan] = useState<Span>(() => {
    try { const v = Number(localStorage.getItem(SPAN_KEY)); if (SPANS.includes(v as Span)) return v as Span; } catch { /* default */ }
    return 12;
  });
  const pickSpan = (v: Span) => { setSpan(v); try { localStorage.setItem(SPAN_KEY, String(v)); } catch { /* not remembered */ } };
  const id = useId();

  if (error) return <div className={className}><ErrorState title="The trend could not be loaded" detail={error} onRetry={onRetry} /></div>;

  const picked = (months ?? []).slice(-span);
  const hasData = (m: TrendMonth) => m.raised > 0 || m.certified > 0 || !!m.treatments;
  const empty = !loading && picked.every((m) => !hasData(m));
  // Start where the data starts (owner, 29 Oct 2026): a year of flat zeros
  // before the first examination says nothing. At least three months stay,
  // so a single busy month still reads as a line, not a dot.
  const first = picked.findIndex(hasData);
  const rows = empty || first <= 0 ? picked : picked.slice(Math.min(first, Math.max(picked.length - 3, 0)));

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-small text-neutral-700" aria-label="Series">
          {LINES.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5"><Swatch shape={s.marker} color={s.color} />{s.label}</li>
          ))}
          <li className="flex items-center gap-1.5"><Swatch color={CERTIFIED.fill} area />{CERTIFIED.label}</li>
        </ul>
        <div className="flex items-center gap-2">
        <div role="group" aria-label="Period" className="flex rounded-btn border border-neutral-200 text-caption">
          {SPANS.map((v) => (
            <button
              key={v} type="button" aria-pressed={span === v} onClick={() => pickSpan(v)}
              title={`Last ${v} months`}
              className={cn('px-2.5 py-1 first:rounded-l-btn last:rounded-r-btn max-md:min-h-9',
                span === v ? 'bg-primary-100 font-medium text-primary-700' : 'text-neutral-600 hover:bg-neutral-50')}
            >
              {v}M<span className="sr-only"> — last {v} months</span>
            </button>
          ))}
        </div>
        <div role="group" aria-label="View as" className="flex rounded-btn border border-neutral-200 text-caption">
          {(['chart', 'table'] as const).map((v) => (
            <button
              key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}
              className={cn('px-2.5 py-1 capitalize first:rounded-l-btn last:rounded-r-btn max-md:min-h-9',
                view === v ? 'bg-primary-100 font-medium text-primary-700' : 'text-neutral-600 hover:bg-neutral-50')}
            >
              {v}
            </button>
          ))}
        </div>
        </div>
      </div>

      {loading && !months ? (
        <div className="h-60 animate-pulse rounded bg-neutral-100" aria-busy="true" aria-label="Loading the trend" />
      ) : view === 'table' ? (
        <TrendTable rows={rows} />
      ) : (
        <div className="relative">
          <Plot rows={rows} labelId={id} />
          {empty && (
            <p className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-small text-neutral-500">
              No examinations or treatments in the last {span} months.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** The lines, in legend order. The mix donut uses `SERIES` alone. */
const LINES = [...SERIES, TREATMENTS];

const H = 240;
const TOP = 12;
const BOTTOM = 28;

function Plot({ rows, labelId }: { rows: TrendMonth[]; labelId: string }) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const narrow = width < 480;
  const LEFT = 36;
  // Room for "123 Pre-employment" at the line ends on a desktop; on a phone
  // the end labels are dropped and the plot takes the width.
  const RIGHT = narrow ? 16 : 118;

  const max = niceMax(Math.max(0, ...rows.flatMap((m) => [m.preEmployment, m.periodic, m.other, m.certified, m.treatments ?? 0])));
  const y = yScale(max, TOP, H - BOTTOM);
  const xs = xPositions(rows.length, LEFT, Math.max(LEFT + 1, width - RIGHT));
  const base = H - BOTTOM;
  const n = rows.length;

  const lines = LINES.map((s) => ({
    ...s,
    points: rows.map((m, i) => ({ x: xs[i]!, y: y(m[s.key] ?? 0) })),
    last: rows[n - 1]?.[s.key] ?? 0,
  }));
  const area = rows.map((m, i) => ({ x: xs[i]!, y: y(m.certified) }));
  // The end labels, pushed apart where lines meet — and the whole stack
  // lifted back inside the plot when the lowest would sit on the axis text.
  const spread = spreadLabels(lines.map((l) => l.points[n - 1]?.y ?? base), 14);
  const overflow = Math.max(0, Math.max(...spread, base - 4) - (base - 4));
  const lastY = spread.map((v) => Math.max(TOP + 4, v - overflow));

  const summary = rows.length
    ? `${rows[0]!.label} to ${rows[n - 1]!.label}: ${formatCount(rows.reduce((t, m) => t + m.raised, 0))} examinations raised, ${formatCount(rows.reduce((t, m) => t + m.certified, 0))} signed off.`
    : 'No months to show.';

  return (
    <div ref={ref} className="w-full">
      <svg
        width={width} height={H} viewBox={`0 0 ${width} ${H}`} role="img" aria-labelledby={`${labelId}-t`}
        className="block max-w-full select-none text-caption" onMouseLeave={() => setHover(null)}
      >
        <title id={`${labelId}-t`}>Health checkup trend by month. {summary}</title>

        {ticks(max, 4).map((t) => (
          <g key={t}>
            <line x1={LEFT} x2={Math.max(LEFT, width - RIGHT)} y1={y(t)} y2={y(t)} stroke={CHROME.grid} strokeWidth="1" />
            <text x={LEFT - 6} y={y(t) + 4} textAnchor="end" fill={CHROME.tick} fontSize="11">{formatCount(t)}</text>
          </g>
        ))}
        <line x1={LEFT} x2={Math.max(LEFT, width - RIGHT)} y1={base} y2={base} stroke={CHROME.axis} strokeWidth="1" />

        {/* Every month on a desktop; every other one, counted back from the
            latest, on a phone, where twelve would overlap. */}
        {rows.map((m, i) => (
          (!narrow || (n - 1 - i) % 2 === 0) && (
            <text
              key={m.key} x={xs[i]} y={H - 8} textAnchor="middle" fontSize="11"
              fill={hover === i ? CHROME.strong : CHROME.tick} fontWeight={hover === i ? 600 : 400}
            >
              {m.label}
            </text>
          )
        ))}

        {/* Soft fills under each wave, fading to nothing at the axis. */}
        <defs>
          {lines.map((l) => (
            <linearGradient key={l.key} id={`${labelId}-g-${l.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={l.color} stopOpacity="0.18" />
              <stop offset="100%" stopColor={l.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {/* Re-keyed on the months shown, so switching 3M/6M/12M draws again. */}
        <g key={rows.map((r) => r.key).join(',')}>
          {area.length > 0 && (
            <path d={smoothAreaPath(area, base)} fill={CERTIFIED.fill} opacity="0.45" className="chart-fade" />
          )}
          {area.length > 0 && (
            <path d={smoothPath(area)} fill="none" stroke={CERTIFIED.stroke} strokeWidth="1" opacity="0.7" pathLength={1} className="chart-draw" />
          )}

          {lines.map((l) => (
            l.last > 0 || l.points.some((p) => p.y < base)
              ? <path key={`a-${l.key}`} d={smoothAreaPath(l.points, base)} fill={`url(#${labelId}-g-${l.key})`} className="chart-fade" />
              : null
          ))}
          {lines.map((l, k) => (
            <path key={l.key} d={smoothPath(l.points)} fill="none" stroke={l.color} strokeWidth="2.25" strokeLinejoin="round" strokeLinecap="round"
              pathLength={1} className="chart-draw" style={{ animationDelay: `${k * 90}ms` }} />
          ))}
          <g className="chart-fade" style={{ animationDelay: '700ms' }}>
            {lines.map((l) => l.points.map((p, i) => (
              <Marker key={`${l.key}-${i}`} shape={l.marker} x={p.x} y={p.y} color={l.color} r={hover === i ? 4.5 : 3} />
            )))}
          </g>
        </g>

        {/* Direct labels at the line ends: the latest month's value, in text ink. */}
        {!narrow && lines.map((l, k) => (
          <text key={l.key} x={xs[n - 1]! + 10} y={lastY[k]! + 4} fontSize="11" fill={CHROME.text}>
            <tspan fontWeight={600} fill={CHROME.strong}>{formatCount(l.last)}</tspan> {l.label}
          </text>
        ))}

        {hover !== null && rows[hover] && (
          <line x1={xs[hover]} x2={xs[hover]} y1={TOP} y2={base} stroke={CHROME.axis} strokeWidth="1" />
        )}

        {/* Hit columns, each with its own title for a touch or a screen reader. */}
        {rows.map((m, i) => {
          const left = i === 0 ? LEFT : (xs[i - 1]! + xs[i]!) / 2;
          const right = i === n - 1 ? width - RIGHT : (xs[i]! + xs[i + 1]!) / 2;
          return (
            <rect
              key={m.key} x={left} y={TOP} width={Math.max(0, right - left)} height={base - TOP} fill="transparent"
              onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} tabIndex={0}
              aria-label={describe(m)}
            >
              <title>{describe(m)}</title>
            </rect>
          );
        })}

        {hover !== null && rows[hover] && (
          <Tooltip m={rows[hover]!} x={xs[hover]!} right={Math.max(LEFT, width - RIGHT)} />
        )}
      </svg>
    </div>
  );
}

const MONTH_LONG: Record<string, string> = {
  Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April', May: 'May', Jun: 'June',
  Jul: 'July', Aug: 'August', Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
};

function longLabel(m: TrendMonth): string {
  const [mon, yy] = m.label.split(' ');
  return `${MONTH_LONG[mon ?? ''] ?? mon} 20${yy}`;
}

function describe(m: TrendMonth): string {
  return `${longLabel(m)}: ${LINES.map((s) => `${s.label} ${formatCount(m[s.key] ?? 0)}`).join(', ')}, signed off ${formatCount(m.certified)}.`;
}

/** Beside the hovered month, flipped to its left when it would cover the line-end labels. */
function Tooltip({ m, x, right }: { m: TrendMonth; x: number; right: number }) {
  const W = 168;
  const lineH = 16;
  const Hh = 10 + lineH * (LINES.length + 2);
  const left = x + 12 + W > right ? x - 12 - W : x + 12;
  const rows = [
    ...LINES.map((s) => ({ label: s.label, value: m[s.key] ?? 0, color: s.color })),
    { label: CERTIFIED.label, value: m.certified, color: CERTIFIED.stroke },
  ];
  return (
    <g pointerEvents="none">
      <rect x={left} y={TOP} width={W} height={Hh} rx="6" fill={CHROME.tooltip} stroke={CHROME.grid} />
      <text x={left + 10} y={TOP + 16} fontSize="11" fontWeight={600} fill={CHROME.strong}>{longLabel(m)}</text>
      {rows.map((r, i) => (
        <g key={r.label}>
          <circle cx={left + 14} cy={TOP + 16 + lineH * (i + 1) - 4} r="3.5" fill={r.color} />
          <text x={left + 24} y={TOP + 16 + lineH * (i + 1)} fontSize="11" fill={CHROME.text}>{r.label}</text>
          <text x={left + W - 10} y={TOP + 16 + lineH * (i + 1)} fontSize="11" textAnchor="end" fontWeight={600} fill={CHROME.strong}>
            {formatCount(r.value)}
          </text>
        </g>
      ))}
    </g>
  );
}

function TrendTable({ rows }: { rows: TrendMonth[] }) {
  const sum = (k: keyof TrendMonth) => rows.reduce((t, m) => t + (m[k] as number), 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-small">
        <thead>
          <tr className="border-b border-neutral-200 text-left text-caption uppercase tracking-wider text-neutral-500">
            <th scope="col" className="py-2 pr-3 font-semibold">Month</th>
            {SERIES.map((s) => <th key={s.key} scope="col" className="py-2 px-3 text-right font-semibold">{s.label}</th>)}
            <th scope="col" className="py-2 px-3 text-right font-semibold">Raised</th>
            <th scope="col" className="py-2 px-3 text-right font-semibold">Signed off</th>
            <th scope="col" className="py-2 pl-3 text-right font-semibold">{TREATMENTS.label}</th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {rows.map((m) => (
            <tr key={m.key} className="border-b border-neutral-100">
              <th scope="row" className="py-1.5 pr-3 text-left font-medium text-neutral-800">{longLabel(m)}</th>
              {SERIES.map((s) => <td key={s.key} className="py-1.5 px-3 text-right text-neutral-700">{formatCount(m[s.key])}</td>)}
              <td className="py-1.5 px-3 text-right font-medium text-neutral-900">{formatCount(m.raised)}</td>
              <td className="py-1.5 px-3 text-right text-neutral-700">{formatCount(m.certified)}</td>
              <td className="py-1.5 pl-3 text-right text-neutral-700">{formatCount(m.treatments ?? 0)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="tabular-nums">
          <tr>
            <th scope="row" className="py-2 pr-3 text-left font-semibold text-neutral-900">Total</th>
            {SERIES.map((s) => <td key={s.key} className="py-2 px-3 text-right font-semibold text-neutral-900">{formatCount(sum(s.key))}</td>)}
            <td className="py-2 px-3 text-right font-semibold text-neutral-900">{formatCount(sum('raised'))}</td>
            <td className="py-2 px-3 text-right font-semibold text-neutral-900">{formatCount(sum('certified'))}</td>
            <td className="py-2 pl-3 text-right font-semibold text-neutral-900">{formatCount(sum('treatments'))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
